/**
 * CertificatesSection Component
 *
 * Component for displaying and managing certificates and diplomas
 * Based on Figma design: node-id=1-1448
 */

'use client';

import { useRef } from 'react';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Icon } from '@/src/shared/ui/icons/Icon';
import { SecondaryButton } from '@/src/shared/ui/button';
import { buildStaticUrl } from '@/src/shared/api';

export interface Certificate {
  id: string;
  imageUrl: string;
  title?: string;
}

export interface CertificatesSectionProps {
  certificates: Certificate[];
  onCertificatesChange: (certificates: Certificate[]) => void;
  onAddCertificates: (files: FileList) => void | Promise<void>;
  onDeleteCertificate: (id: string) => void;
  disabled?: boolean;
}

export function CertificatesSection({
  certificates,
  onDeleteCertificate,
  onAddCertificates,
  disabled = false,
}: CertificatesSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddPhotosClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onAddCertificates(e.target.files);
      // Reset input value to allow selecting the same file again
      e.target.value = '';
    }
  };

  return (
    <div>
      <Typography variant="body" weight="medium" className="mb-4">
        Certificates and diplomas
      </Typography>

      {/* Certificates Grid */}
      <div className="flex flex-wrap gap-4 mb-4">
        {certificates.map((certificate) => (
          <div key={certificate.id} className="relative group">
            <img
              src={buildStaticUrl(certificate.imageUrl)}
              alt={certificate.title || 'Certificate'}
              className="w-24 h-24 object-cover rounded-xl border border-border-primary"
            />
            {/* Delete Button */}
            <button
              type="button"
              onClick={() => onDeleteCertificate(certificate.id)}
              disabled={disabled}
              className="absolute top-1 right-1 p-1.5 bg-white rounded-full shadow-md
                opacity-0 group-hover:opacity-100 transition-opacity
                hover:bg-background-secondary
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="x" size="xs" color="secondary" />
            </button>
          </div>
        ))}

        {/* Empty Placeholder Slots */}
        {certificates.length < 4 &&
          Array.from({ length: Math.min(3, 4 - certificates.length) }).map((_, index) => (
            <div
              key={`placeholder-${index}`}
              className="w-24 h-24 rounded-xl border-2 border-dashed border-border-primary
                bg-surface-sunken flex items-center justify-center cursor-pointer
                hover:border-accent-primary hover:bg-accent-primary/5 transition-colors"
              onClick={handleAddPhotosClick}
            >
              <Icon name="plus" size="md" color="secondary" />
            </div>
          ))}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled}
      />

      {/* Add Photos Button */}
      <div className="flex justify-center">
        <SecondaryButton
          onClick={handleAddPhotosClick}
          disabled={disabled}
          className="border-accent-primary text-accent-primary hover:bg-accent-primary/5"
        >
          <Icon name="plus" size="sm" className="mr-2" />
          Add Photos
        </SecondaryButton>
      </div>
    </div>
  );
}
